import { FC, useState } from "react"
import { Alert, Button, MenuItem, Stack, TextField } from "@mui/material"
import { Modal } from "@/core/components"
import { getApiErrorMessage } from "@/core/utils"
import { UserbotBatches } from "../../interfaces"
import { useImportProfileDictionariesMutation } from "../../store"
import { PROFILE_DICT_KIND_LABELS } from "../../constants/dictionaryKinds"
import { countImportValues, parseImportValues } from "./importValues"

const IMPORT_MAX = 10000

interface IProps {
  onClose: () => void
}

const ImportDictionariesForm: FC<IProps> = ({ onClose }) => {
  const [kind, setKind] = useState<UserbotBatches.ProfileDictKind>(
    UserbotBatches.PROFILE_DICT_KINDS[0],
  )
  const [raw, setRaw] = useState("")
  const [result, setResult] =
    useState<UserbotBatches.ImportProfileDictRes | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [importDict, { isLoading }] = useImportProfileDictionariesMutation()

  // Значения отправляются как есть (пустые/дубликаты — сервер считает в
  // skipped); лимит DTO проверяем по непустым (спека §7).
  const values = parseImportValues(raw)
  const nonEmpty = countImportValues(values)
  const canSubmit = nonEmpty >= 1 && nonEmpty <= IMPORT_MAX

  const handleSubmit = async () => {
    setError(null)
    try {
      const res = await importDict({ body: { kind, values } }).unwrap()
      setResult(res)
    } catch (e) {
      setError(getApiErrorMessage(e))
    }
  }

  return (
    <Stack spacing={2}>
      <TextField
        select
        label="Тип словаря"
        value={kind}
        onChange={(e) =>
          setKind(e.target.value as UserbotBatches.ProfileDictKind)
        }
        fullWidth
      >
        {UserbotBatches.PROFILE_DICT_KINDS.map((k) => (
          <MenuItem key={k} value={k}>
            {PROFILE_DICT_KIND_LABELS[k]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Значения (по одному на строку)"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        multiline
        minRows={8}
        fullWidth
        helperText={
          canSubmit
            ? `Непустых значений: ${nonEmpty}`
            : "Должно быть от 1 до 10 000 непустых значений"
        }
      />
      {result && (
        <Alert severity="success">
          {`Загружено: ${result.imported}, пропущено: ${result.skipped}`}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={onClose}>Закрыть</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || isLoading}
          onClick={handleSubmit}
        >
          Импортировать
        </Button>
      </Stack>
    </Stack>
  )
}

export const ImportDictionariesModal: FC<IProps> = ({ onClose }) => {
  return (
    <Modal
      disableBackdropClose
      title="Импорт списком"
      onClose={onClose}
      element={<ImportDictionariesForm onClose={onClose} />}
    />
  )
}
